//region :: Imports ::
import {createRef, ReactElement, ReactNode, RefObject} from 'react';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MComponent from '../Toolkit/Components/Component';
import MForm, {MFormOutcome} from '../Toolkit/Components/Controls/Form';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {CreateWorkerRequest, WorkerType} from '../Api/WorkerHubClients';
import {Worker, WorkerValidator} from '../Services/WorkerHubTypes';
import WorkerService from '../Services/WorkerService';
import {
    getEnumItems,
    MEnumItem,
    MNullable,
    toErrorMessage
} from '../Toolkit/Utils';
import MAlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import AlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import WithRouterProps from '../Toolkit/WithRouterProps';
import {CloseIconName, NavigateToIconName, SaveIconName, SearchIconName, SuccessIcon} from '../Toolkit/Icons';
import MButton from '../Toolkit/Components/Controls/Button';
import WithRouter from '../Toolkit/WithRouter';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MFormFieldControl from '../Toolkit/Components/Controls/FormFieldControl';
import MCalendar from '../Toolkit/Components/Controls/Calendar';
import MTextBox from '../Toolkit/Components/Controls/TextBox';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import MDynEntityLookup from '../Toolkit/Components/Controls/Dynamics/DynEntityLookup';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import MFormSeparator from '../Toolkit/Components/Controls/FormSeparator';

//endregion

/** */
type RouteParams = {};

/** */
type WorkerFormData = {
    firstName: string,
    lastName: string,
    workerType: MNullable<WorkerType>,
    organizationUnit: string,
    jobTitle: string,
    resourceManager: string,
    startDate: MNullable<Date>,
    address: string,
    postCode: string,
    emailAddress: string,
    workPhoneNumber: string,
    personalPhoneNumber: string,
    contractingAgency: string,
    thirdPartySupplier: string,
    dateOfBirth: MNullable<Date>
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    page: MPageProps;
    workerTypeItems: MDropDownItem<WorkerType>[];
    form: {
        id: string;
        data: WorkerFormData;
        isSubmitting: boolean;
        validationSchema: any;
    }
}

/** */
class CreateWorkerPage extends MComponent<Props, State> {

    private m_newWorker: MNullable<Worker>;
    private readonly m_workerService: WorkerService;
    private readonly m_fieldServiceUrl: string;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dialogRef: RefObject<MAlertDialog>;
    private readonly m_formRef: RefObject<MForm<WorkerFormData>>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_newWorker = null;
        this.m_workerService = new WorkerService(this.props.msalContext);
        this.m_fieldServiceUrl = process.env.REACT_APP_D365_FIELDSERVICE_HOST_URL!;
        this.m_pageRef = createRef<MPage>();
        this.m_dialogRef = createRef<MAlertDialog>();
        this.m_formRef = createRef<MForm<WorkerFormData>>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.showWorkerCreatedDialog = this.showWorkerCreatedDialog.bind(this);
        this.isFormDisabled = this.isFormDisabled.bind(this);
        this.isAddressDisabled = this.isAddressDisabled.bind(this);
        this.isPostCodeDisabled = this.isPostCodeDisabled.bind(this);
        this.closeButton_onClick = this.closeButton_onClick.bind(this);
        this.navigateToWorkerButton_onClick = this.navigateToWorkerButton_onClick.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);

    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {locked: false};

        //  Worker Types
        const workerTypeItems = getEnumItems(WorkerType).map((item: MEnumItem) => {
            return {label: item.name, value: item.value};
        });

        //  Form Data
        const data: WorkerFormData = {
            firstName: '',
            lastName: '',
            workerType: null,
            organizationUnit: '',
            jobTitle: '',
            resourceManager: '',
            startDate: null,
            address: '',
            postCode: '',
            emailAddress: '',
            workPhoneNumber: '',
            personalPhoneNumber: '',
            contractingAgency: '',
            thirdPartySupplier: '',
            dateOfBirth: null
        };

        //  Form Validation
        const validationSchema: any = Yup.object().shape({
            firstName: WorkerValidator.firstName,
            lastName: WorkerValidator.lastName,
            workerType: WorkerValidator.workerType,
            organizationUnit: WorkerValidator.organizationUnit,
            jobTitle: WorkerValidator.jobTitle,
            resourceManager: WorkerValidator.resourceManager,
            startDate: WorkerValidator.startDate,
            address: WorkerValidator.address,
            postCode: WorkerValidator.postCode,
            emailAddress: WorkerValidator.emailAddress,
            workPhoneNumber: WorkerValidator.workPhoneNumber,
            personalPhoneNumber: WorkerValidator.personalPhoneNumber,
            contractingAgency: WorkerValidator.contractingAgency,
            thirdPartySupplier: WorkerValidator.thirdPartySupplier,
            dateOfBirth: WorkerValidator.dateOfBirth
        });

        this.state = {
            page: page,
            workerTypeItems: workerTypeItems,
            form: {
                id: 'workerForm',
                data: data,
                isSubmitting: false,
                validationSchema: validationSchema
            }
        };

    }

    /** */
    private static getAddressHelp(): ReactNode {

        return (
            <span>
                <strong>Employees</strong> will be assigned a default address on creation.
                For <strong>Contractors/Subcontractors</strong>, use their home address.
            </span>
        );

    }

    /** */
    private static getPostCodeHelp(): ReactNode {

        return (
            <span>
                <strong>Employees</strong> will be assigned a default address on creation.
                For <strong>Contractors/Subcontractors</strong>, use their home postcode.
            </span>
        );

    }

    /** */
    private static getEmailHelp(): ReactNode {

        return (
            <span>
                An MJ Quinn email address should be used for <strong>Employees</strong>.
                For <strong>Contractors/Subcontractors</strong>, use their personal email address.
            </span>
        );

    }

    /** */
    private showWorkerCreatedDialog(): void {

        this.m_dialogRef.current!.show(
            'Create Worker',
            `A new worker has been created for ${this.m_newWorker!.firstName} ${this.m_newWorker!.lastName}.`,
            SuccessIcon,
            <MButton label="Open new worker"
                     color="success"
                     icon={NavigateToIconName}
                     disabled={false}
                     onClick={this.navigateToWorkerButton_onClick}/>);

    }

    /** */
    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    /** */
    private isAddressDisabled(): boolean {

        const workerType: MNullable<WorkerType> = this.m_formRef.current?.getFieldValue('workerType');
        return this.isFormDisabled() || workerType === WorkerType.Employee;

    }

    /** */
    private isPostCodeDisabled(): boolean {

        const workerType: MNullable<WorkerType> = this.m_formRef.current?.getFieldValue('workerType');
        return this.isFormDisabled() || workerType === WorkerType.Employee;

    }

    /** */
    private closeButton_onClick(): void {
        this.props.history.goBack();
    }

    /** */
    private navigateToWorkerButton_onClick(): void {
        this.props.history.replace(`/workers/${this.m_newWorker!.id}`);
    }

    /** */
    private form_onBeforeSubmit(fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>): boolean {

        const cancel = false;
        this.m_pageRef.current!.messageBar.close();
        this.safeSetState((draft: State) => draft.form.isSubmitting = true);
        return cancel;

    }

    /** */
    private async form_onSubmit(fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>): Promise<void> {

        const address = fieldValues.workerType === WorkerType.Employee ? '' : fieldValues.address;
        const postCode = fieldValues.workerType === WorkerType.Employee ? '' : fieldValues.postCode;
        const result = await this.m_workerService
                                 .create(new CreateWorkerRequest({
                                     firstName: fieldValues.firstName,
                                     lastName: fieldValues.lastName,
                                     workerType: fieldValues.workerType!,
                                     organizationUnitName: fieldValues.organizationUnit,
                                     jobTitleName: fieldValues.jobTitle,
                                     resourceManagerName: fieldValues.resourceManager,
                                     startDate: fieldValues.startDate!,
                                     homeAddress: address,
                                     homePostCode: postCode,
                                     emailAddress: fieldValues.emailAddress,
                                     workPhoneNumber: fieldValues.workPhoneNumber,
                                     personalPhoneNumber: fieldValues.personalPhoneNumber,
                                     contractingAgencyName: fieldValues.contractingAgency,
                                     thirdPartySupplierName: fieldValues.thirdPartySupplier,
                                     dateOfBirth: fieldValues.dateOfBirth!
                                 }));

        if(result.hasErrors) {
            throw result.exception;
        }

        this.m_newWorker = result.reply!.worker;

    }

    /** */
    private form_onError(exception: any): boolean {

        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;

    }

    /** */
    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: WorkerFormData, formikHelpers: FormikHelpers<WorkerFormData>, exception: any | undefined): void {

        if(outcome === 'success') {

            this.showWorkerCreatedDialog();
            return;

        }

        this.safeSetState(
            (draft: State) => draft.form.isSubmitting = false);

    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={SaveIconName}
                         iconOnly={true}
                         isBusy={this.isFormDisabled}
                         disabled={false}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={CloseIconName}
                         disabled={this.isFormDisabled}
                         onClick={this.closeButton_onClick}/>
            </MToolbar>
        );

    }

    /** */
    public render(): ReactElement {

        return (
            <MPage title="Create Worker"
                   subtitle="Create a new Employee or Contract worker"
                   locked={this.state.page.locked}
                   width={8}
                   toolbar={this.getToolbarTemplate()}
                   ref={this.m_pageRef}>

                <AlertDialog ref={this.m_dialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.data}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onError={this.form_onError}
                       onAfterSubmit={this.form_onAfterSubmit}
                       ref={this.m_formRef}>

                    <div className="formgrid grid">

                        <div className="col-12">
                            <MFormSeparator title="General"
                                            hasTopMargin={false}/>
                        </div>

                        {/* First Name */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="First Name"
                                               fieldName="firstName">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={100}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isFormDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Last Name */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Last Name"
                                               fieldName="lastName">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={100}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isFormDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Worker Type */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Worker Type"
                                               fieldName="workerType">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDropDown id={fieldProps.field.name}
                                                   items={this.state.workerTypeItems}
                                                   fieldProps={fieldProps}
                                                   disabled={this.isFormDisabled}
                                                   className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Organisation Unit */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Organisation Unit"
                                               fieldName="organizationUnit">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_fieldServiceUrl}
                                                          entityLogicalName="msdyn_organizationalunit"
                                                          searchFieldName="msdyn_name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Job Title */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Job Title"
                                               fieldName="jobTitle">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_fieldServiceUrl}
                                                          entityLogicalName="bookableresourcecategory"
                                                          searchFieldName="name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Resource Manager */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Resource Manager"
                                               fieldName="resourceManager">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_fieldServiceUrl}
                                                          entityLogicalName="bookableresource"
                                                          searchFieldName="name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Start Date */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Start Date"
                                               fieldName="startDate">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MCalendar id={fieldProps.field.name}
                                                   fieldProps={fieldProps}
                                                   disabled={this.isFormDisabled}
                                                   className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Date Of Birth */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Date Of Birth"
                                               fieldName="dateOfBirth">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MCalendar id={fieldProps.field.name}
                                                   fieldProps={fieldProps}
                                                   disabled={this.isFormDisabled}
                                                   className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Padding */}
                        <div className="md:col-8"/>

                        <div className="col-12">
                            <MFormSeparator title="Contact Details"/>
                        </div>

                        {/* Address */}
                        <div className="col-12 md:col-8">
                            <MFormFieldControl label="Address"
                                               fieldName="address"
                                               help={CreateWorkerPage.getAddressHelp()}>

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={2000}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isAddressDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Post Code */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Post Code"
                                               fieldName="postCode"
                                               help={CreateWorkerPage.getPostCodeHelp()}>

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={15}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isPostCodeDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Email */}
                        <div className="col-12 md:col-8">
                            <MFormFieldControl label="Email"
                                               fieldName="emailAddress"
                                               help={CreateWorkerPage.getEmailHelp()}
                                               icon="pi-envelope">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={100}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isFormDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Padding */}
                        <div className="md:col-4"/>

                        {/* Work Phone Number */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Work Mobile Number"
                                               fieldName="workPhoneNumber"
                                               icon="pi-phone">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={29}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isFormDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Personal Phone Number */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Personal Phone Number"
                                               fieldName="personalPhoneNumber"
                                               icon="pi-phone">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MTextBox id={fieldProps.field.name}
                                                  maxLength={29}
                                                  fieldProps={fieldProps}
                                                  disabled={this.isFormDisabled}
                                                  className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Padding */}
                        <div className="md:col-4"/>

                        <div className="col-12">
                            <MFormSeparator title="Payment Details"/>
                        </div>

                        {/* Contracting Agency */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="Contracting Agency"
                                               fieldName="contractingAgency">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_fieldServiceUrl}
                                                          entityLogicalName="account"
                                                          searchFieldName="name"
                                                          searchFilter="statuscode eq 1 and qtq_accounttype eq 958460000"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* 3rd Party Supplier */}
                        <div className="col-12 md:col-4">
                            <MFormFieldControl label="3rd Party Supplier"
                                               fieldName="thirdPartySupplier">

                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <MDynEntityLookup id={fieldProps.field.name}
                                                          environmentUrl={this.m_fieldServiceUrl}
                                                          entityLogicalName="account"
                                                          searchFieldName="name"
                                                          searchFilter="statuscode eq 1"
                                                          authContext={this.props.msalContext}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}/>
                                    );

                                }}

                            </MFormFieldControl>
                        </div>

                        {/* Padding */}
                        <div className="md:col-4"/>

                    </div>

                </MForm>

            </MPage>
        );

    }

}

export default withMsal(WithRouter(CreateWorkerPage));
